import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: {
        sitekey: string
        callback?: (token: string) => void
        'error-callback'?: () => void
        'expired-callback'?: () => void
        theme?: 'light' | 'dark' | 'auto'
        size?: 'normal' | 'compact'
      }) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface TurnstileProps {
  siteKey: string
  onSuccess: (token: string) => void
  onError?: () => void
  onExpire?: () => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact'
  className?: string
}

export function Turnstile({
  siteKey,
  onSuccess,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
  className = ''
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const scriptLoadedRef = useRef(false)
  const [isLoaded, setIsLoaded] = useState(false)
  
  // Use refs for callbacks to prevent re-renders
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const onExpireRef = useRef(onExpire)
  
  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
    onExpireRef.current = onExpire
  }, [onSuccess, onError, onExpire])

  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]')
    if (existingScript || scriptLoadedRef.current) {
      if (window.turnstile) {
        setIsLoaded(true)
        return
      }
      // Script exists but turnstile not loaded yet, wait for it with timeout
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max wait
      const checkInterval = setInterval(() => {
        attempts++
        if (window.turnstile) {
          scriptLoadedRef.current = true
          setIsLoaded(true)
          clearInterval(checkInterval)
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval)
          console.warn('Turnstile script loaded but API not available')
        }
      }, 100)
      return () => clearInterval(checkInterval)
    }

    // Load Turnstile script
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.id = 'turnstile-script'
    
    script.onload = () => {
      scriptLoadedRef.current = true
      setIsLoaded(true)
    }
    
    script.onerror = () => {
      console.error('Failed to load Turnstile script')
      scriptLoadedRef.current = false
    }
    
    document.body.appendChild(script)

    return () => {
      // Only cleanup widget, not the script (it's shared across components)
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !window.turnstile || !siteKey) {
      return
    }

    // Don't re-render if widget already exists and siteKey hasn't changed
    if (widgetIdRef.current) {
      // Only re-render if siteKey changed
      const currentSiteKey = (containerRef.current as any)?.dataset?.siteKey
      if (currentSiteKey === siteKey) {
        return
      }
      // Site key changed, remove old widget
      try {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      } catch (e) {
        // Ignore errors
      }
    }

    // Clear container before rendering
    if (containerRef.current) {
      // Clear innerHTML safely
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild)
      }
      // Store siteKey in dataset to track changes
      containerRef.current.setAttribute('data-site-key', siteKey)
    }

    // Render new widget
    try {
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onSuccessRef.current(token)
        },
        'error-callback': () => {
          console.warn('Turnstile error occurred')
          if (onErrorRef.current) {
            onErrorRef.current()
          }
        },
        'expired-callback': () => {
          console.warn('Turnstile token expired')
          if (onExpireRef.current) {
            onExpireRef.current()
          }
        },
        theme,
        size
      })
      widgetIdRef.current = widgetId
    } catch (error) {
      console.error('Failed to render Turnstile widget:', error)
    }
  }, [isLoaded, siteKey, theme, size]) // Removed callbacks from dependencies

  if (!siteKey) {
    return null
  }

  return (
    <div className={className} data-turnstile-widget>
      <div ref={containerRef} className="flex justify-center" />
    </div>
  )
}

